import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { CardContainer, ListItem } from "@msys/ui";
import { Box, Button, Stack, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Link } from "react-router-dom";
import { ProjectStateMachineStatus } from "../../../../clients/graphqlTypes.js";
import { RestrictedByOrganisationPermissionWithDebug } from "../../../auth/RestrictedByOrganisationPermission.js";
import { useUserData } from "../../../auth/useUserData.js";
import { ProjectIcon } from "../../../features/projects/ProjectIcon.js";
import { CreateProjectButton } from "../../../features/projects/buttons/CreateProjectButton.js";
import { ProjectListItem } from "../../../features/projects/components/ProjectListItem.js";
import { useDashboardProjectsQuery } from "./DashboardProjectsBox.generated.js";

export function DashboardProjectsBox() {
  const { t } = useTranslate(["Projects", "Global"]);
  const viewer = useUserData().currentUser!;

  const allowedStates: ProjectStateMachineStatus[] = viewer.organisation
    .isClientOrganisation
    ? ["contracted", "opportunity"]
    : ["contracted"];

  const client = useApolloClient();
  const query = useDashboardProjectsQuery({
    client,
    fetchPolicy: "cache-and-network",
    variables: {
      filterIncludeState: allowedStates,
    },
  });

  const projectsAll =
    getDataOrNull(query.data?.projectsAll)?.edges.map(e => e.node) ?? [];

  return (
    <CardContainer
      isExpandable
      Icon={<ProjectIcon />}
      title={t("Current projects", { ns: "Projects" })}
      ActionButton={
        <RestrictedByOrganisationPermissionWithDebug permission="organisation_projects_create">
          <CreateProjectButton size="small" type="icon" pageName="Dashboard" />
        </RestrictedByOrganisationPermissionWithDebug>
      }
      isLoading={query.loading}
    >
      {projectsAll.length === 0 && (
        <Box
          sx={{
            p: 1,
            py: 3,
          }}
        >
          <Typography align="center" sx={{ color: "grey.500" }} variant="body2">
            {t("There are no items to display", { ns: "Global" })}
          </Typography>
        </Box>
      )}
      {projectsAll.length > 0 && (
        <Stack
          direction="column"
          spacing={0}
          sx={{
            width: "100%",
          }}
        >
          {projectsAll.map(project => (
            <ListItem
              key={project.id}
              component={Link}
              //@ts-ignore
              to={`/projects/${project.id}`}
            >
              <ProjectListItem
                project={project}
                showAssignee
                showStatus={allowedStates.length > 1}
              />
            </ListItem>
          ))}
        </Stack>
      )}
      {projectsAll.length > 0 && (
        <Box>
          <Button
            component={Link}
            to={{
              pathname: `/projects/current`,
            }}
            color="secondary"
            variant="text"
            fullWidth
          >
            {t("See all", { ns: "Global" })}
          </Button>
        </Box>
      )}
    </CardContainer>
  );
}
